import React, { useState } from "react";
import { http, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const OPTIONS = [
  ["reminder_days_before", "Days before due date", 0, 30],
  ["reminder_days_after", "First overdue reminder (days)", 1, 60],
  ["reminder_repeat_days", "Repeat overdue every (days)", 1, 30],
  ["reminder_send_hour", "Send after this hour (India time, 0–23)", 0, 23],
  ["reminder_max_per_run", "Maximum messages per check", 1, 50],
];
const DEFAULTS = {
  reminder_days_before: 3,
  reminder_days_after: 3,
  reminder_repeat_days: 7,
  reminder_send_hour: 9,
  reminder_max_per_run: 20,
};

export default function ReminderSettings({ form, set }) {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loadPreview = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await http.get("/settings/reminders/preview");
      setPreview(data);
    } catch (err) { setError(requestErrorMessage(err, "preview eligible reminders")); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    if (!preview || !window.confirm(`Send payment reminders to up to ${Math.min(preview.sendable, form.reminder_max_per_run ?? 20)} customers now? This sends real emails, even if automatic reminders are off.`)) return;
    setBusy(true); setError(""); setResult(null);
    try {
      const { data } = await http.post("/settings/reminders/run");
      setResult(data);
      await loadPreview();
    } catch (err) { setError(requestErrorMessage(err, "send reminders")); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 space-y-5">
      <div>
        <h3 className="font-heading font-semibold">Payment reminders by email</h3>
        <p className="text-sm text-muted-foreground mt-1">Uses the connected Brevo sender for unpaid sales invoices with a due date and a customer email. Each invoice is emailed at most once per stage.</p>
      </div>
      <label className="flex items-start gap-3 rounded-md border p-4 cursor-pointer">
        <input type="checkbox" checked={form.reminder_enabled === true}
          onChange={(event) => set("reminder_enabled", event.target.checked)}
          className="h-4 w-4 mt-0.5 accent-brand-600" data-testid="settings-reminder_enabled" />
        <span><strong className="text-sm">Enable automatic reminders</strong><span className="block text-xs text-muted-foreground mt-1">Off until you turn it on and save. The daily check sends only after the configured hour.</span></span>
      </label>
      <div className="grid sm:grid-cols-2 gap-4">
        {OPTIONS.map(([key, label, min, max]) => (
          <div key={key}>
            <Label htmlFor={key}>{label}</Label>
            <Input id={key} type="number" min={min} max={max} step="1"
              value={form[key] ?? DEFAULTS[key]}
              onChange={(event) => set(key, event.target.value === "" ? "" : Number(event.target.value))}
              className="mt-1.5" data-testid={`settings-${key}`} />
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">A reminder goes out before the due date, on the due date, then at the chosen overdue interval. Save Changes above before previewing or sending. Manual Send Now works even if automatic sending is off. The checker runs while the app is online; Autoscale may pause when idle, so a separate scheduled worker is needed for guaranteed daily delivery.</p>
      <div className="border-t pt-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={loadPreview} disabled={busy} data-testid="reminders-preview">
            {busy ? "Checking…" : "Preview eligible invoices"}
          </Button>
          <Button type="button" onClick={sendNow} disabled={busy || !preview?.delivery_configured || !preview?.sendable} data-testid="reminders-send-now">Send eligible reminders now</Button>
        </div>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        {preview && (
          <div className="rounded-md bg-slate-50 p-4 text-sm space-y-2">
            <p><strong>{preview.sendable}</strong> customer{preview.sendable === 1 ? "" : "s"} can receive a reminder today ({preview.timezone}).</p>
            <p className="text-xs text-muted-foreground">Already sent: {preview.already_sent}. No customer email: {preview.missing_email}. Delivery needs review: {preview.uncertain}. Up to {form.reminder_max_per_run ?? 20} will be attempted per check.</p>
            {!preview.delivery_configured && <p className="text-amber-700">Brevo sender is not configured. Sending is unavailable.</p>}
            {preview.invoices?.length > 0 && <ul className="list-disc pl-5 max-h-44 overflow-y-auto">
              {preview.invoices.map((item) => <li key={item.id}>{item.invoice_no} · {item.customer_name} · {item.stage.replaceAll("-", " ")} · ₹{Number(item.balance_due || 0).toFixed(2)}</li>)}
            </ul>}
          </div>
        )}
        {result && <p role="status" className="text-sm text-emerald-700">Sent {result.sent}; skipped {result.skipped}; failed {result.failed}; already sent {result.already_sent}; needing review {result.uncertain}; remaining for another check {result.remaining}.</p>}
      </div>
    </Card>
  );
}